﻿/**
 * Configuración y lógica pura de la bitácora de incidentes (SGSI): metadatos
 * visuales de severidad y estado, columnas de la tabla, orden y plazos.
 * Sin React ni I/O.
 */

import { sortBy } from './sort'

/** Severidades (CHECK `SgsiIncident_Severity_check`): color y peso para ordenar. */
export const INCIDENT_SEVERITY_META = {
  Crítico: { color: '#dc2626', rank: 4, icon: 'fa-circle-exclamation' },
  Alto: { color: '#ea580c', rank: 3, icon: 'fa-arrow-up' },
  Medio: { color: '#ca8a04', rank: 2, icon: 'fa-minus' },
  Bajo: { color: '#64748b', rank: 1, icon: 'fa-arrow-down' },
}

/** Estados (CHECK `SgsiIncident_Status_check`): color, orden del flujo y clave del conteo. */
export const INCIDENT_STATUS_META = {
  Abierto: { color: '#ef4444', rank: 1, countKey: 'abierto' },
  'En curso': { color: '#f59e0b', rank: 2, countKey: 'curso' },
  Cerrado: { color: '#22c55e', rank: 3, countKey: 'cerrado' },
}

const FALLBACK_COLOR = '#64748b'

/** Color de una severidad o estado (gris si no se reconoce). */
export const severityColor = (severity) =>
  INCIDENT_SEVERITY_META[severity]?.color || FALLBACK_COLOR
export const statusColor = (status) =>
  INCIDENT_STATUS_META[status]?.color || FALLBACK_COLOR

/**
 * Indica si un incidente no cerrado ya pasó su plazo de respuesta.
 * @param {Object} incident
 * @param {number} [now=Date.now()]
 * @returns {boolean}
 */
export const isIncidentOverdue = (incident, now = Date.now()) =>
  incident?.Status !== 'Cerrado' &&
  !!incident?.ResponseDueAt &&
  new Date(incident.ResponseDueAt).getTime() < now

/**
 * Columnas de la tabla de incidentes, en orden.
 * @type {Array<{key: string, label: string, className?: string}>}
 */
export const INCIDENT_TABLE_COLUMNS = [
  { key: 'code', label: 'Código' },
  { key: 'severity', label: 'Severidad' },
  { key: 'status', label: 'Estado' },
  { key: 'description', label: 'Descripción' },
  {
    key: 'detectedAt',
    label: 'Detectado',
    className: 'd-none d-md-table-cell',
  },
  {
    key: 'reportedBy',
    label: 'Reportado por',
    className: 'd-none d-lg-table-cell',
  },
  { key: 'assets', label: 'Activos', className: 'd-none d-lg-table-cell' },
]

/** Orden por defecto: los detectados más recientemente primero. */
export const DEFAULT_INCIDENT_SORT = { key: 'detectedAt', dir: 'desc' }

const toTime = (value) => (value ? new Date(value).getTime() : null)

/** Valor por el que se ordena cada columna. */
const INCIDENT_SORT_ACCESSORS = {
  // El correlativo INC-001 se compara con orden numérico natural.
  code: (i) => i.Code || '',
  severity: (i) => INCIDENT_SEVERITY_META[i.Severity]?.rank ?? null,
  status: (i) => INCIDENT_STATUS_META[i.Status]?.rank ?? null,
  description: (i) => i.Description || '',
  detectedAt: (i) => toTime(i.DetectedAt),
  reportedBy: (i) => i.ReportedBy || '',
  assets: (i) => i.assets?.length ?? 0,
}

/** Indica si una columna de incidentes se puede ordenar. */
export const isIncidentSortable = (key) => key in INCIDENT_SORT_ACCESSORS

/**
 * Ordena incidentes por una columna (vacíos al final).
 * @param {Array<Object>} items
 * @param {{key: string, dir: 'asc'|'desc'}} sort
 * @returns {Array<Object>}
 */
export const sortIncidents = (items = [], sort = DEFAULT_INCIDENT_SORT) => {
  const accessor = INCIDENT_SORT_ACCESSORS[sort?.key]
  return accessor ? sortBy(items, accessor, sort.dir) : items
}
